import { Injectable, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ProctoringEvent,
  ViolationSeverity,
  ProctoringConfig,
  ProctoringViolationType,
  TerminationReason,
  ProctoringRule
} from '../models/proctoring.models';
import { AiInterviewSessionService } from './AiInterviewSessionService';

// Assuming face-api.js is installed
import * as faceapi from 'face-api.js';

export interface ViolationState {
  warningCount: number;
  criticalCount: number;
  maxWarnings?: number;
  isTerminated: boolean;
  currentWarning: ProctoringViolationType | null | string;
  terminationReason: TerminationReason | null | string;
}

@Injectable({
  providedIn: 'root'
})
export class ProctoringService implements OnDestroy {
  private videoElement: HTMLVideoElement | null = null;
  private snapshotElement: HTMLVideoElement | null = null;
  // Offscreen canvas used to enhance low-light frames before face detection.
  private enhancementCanvas: HTMLCanvasElement | null = null;
  private enhancementCtx: CanvasRenderingContext2D | null = null;
  private videoStartTime: number | null = null;
  private isDetecting = false;
  private loopId: any;
  private fps = 6;
  private isBrowser: boolean;
  private maxWarnings = 5;
  private config: ProctoringConfig | null = null;
  private tabSwitchTimer: any = null;
  private currentSessionId: string = 'unknown';
  private lastSnapshotAtMap = new Map<string, number>();
  private lastWarningAtMap = new Map<string, number>();
  private gracePeriodMap = new Map<string, number>();
  private readonly GRACE_PERIOD_MS = 3000;

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private audioCheckInterval: ReturnType<typeof setInterval> | null = null;
  private rawAudioStream: MediaStream | null = null;
  private lastDetection: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68> | null = null;
  private lastDetectionAt = 0;

  // Scenario 1 (candidate silent): consecutive frames above absolute threshold.
  private bgVoiceSilentCount = 0;
  private readonly BG_SILENT_FRAME_THRESHOLD = 4;

  // Scenario 2 (both speaking): sliding window, N of last 5 frames above ratio → trigger.
  private bgVoiceWindow: boolean[] = [];
  private readonly BG_VOICE_WINDOW_SIZE = 5;
  private readonly BG_VOICE_WINDOW_HITS = 3;

  // One-time median calibration — collects the candidate's first N speaking frames,
  // computes the median as a fixed baseline immune to noise-floor initialisation issues.
  // No continuous updates: once set, the baseline never changes.
  private calibrationFrames: number[] = [];
  private readonly CALIBRATION_FRAME_COUNT = 20; // 10 s of speaking (20 × 500 ms)
  private calibrationComplete = false;
  private candidateVoiceBaseline = 0;

  // Post-speech grace: ignore audio right after candidate stops speaking.
  private lastCandidateSpeakingAt = 0;
  private readonly POST_SPEECH_GRACE_MS = 1500;

  // Paused while the AI is speaking — prevents room-echo from entering calibration or detection.
  private audioAnalysisPaused = false;


  private destroy$ = new Subject<void>();

  private violationStateSubject = new BehaviorSubject<ViolationState>({
    warningCount: 0,
    criticalCount: 0,
    maxWarnings: 5,
    isTerminated: false,
    currentWarning: null,
    terminationReason: null
  });

  public violationState$ = this.violationStateSubject.asObservable();

  private violationCounters = {
    NO_FACE: 0,
    MULTIPLE_FACES: 0,
    LOOKING_AWAY: 0,
    EYES_CLOSED: 0,
    FACE_TOO_FAR: 0
  };

  private thresholds = {
    warning: 6 * 4, // 4 seconds
    critical: 6 * 8 // 8 seconds
  };

  constructor(
    private sessionService: AiInterviewSessionService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.useDefaultConfig();
  }

  async initializeModels(): Promise<void> {
    if (!this.isBrowser) return;
    try {
      // Hardcoded models path for production-ready approach
      const modelsPath = '/assets/models/';
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(modelsPath),
        faceapi.nets.faceLandmark68Net.loadFromUri(modelsPath)
      ]);
      console.log('Face API Models loaded');
    } catch (error) {
      console.error('Failed to load face-api models', error);
      throw error;
    }
  }

  async initializeConfig(id: string = 'default'): Promise<void> {
    if (!this.isBrowser) return;
    try {
      const res: any = await firstValueFrom(this.sessionService.getProctoringConfig(id));
      if (res && res.Success && res.Data) {
        this.config = res.Data;
        this.maxWarnings = res.Data.total_allowed_violation_count;
        this.updateThresholdsFromConfig();

        const currentState = this.violationStateSubject.value;
        this.violationStateSubject.next({
          ...currentState,
          maxWarnings: this.maxWarnings
        });

        console.log('[Proctoring] Configuration loaded successfully');
      }
    } catch (error) {
      console.warn('[Proctoring] Failed to fetch config, using defaults', error);
      this.useDefaultConfig();
    }
  }

  private useDefaultConfig(): void {
    this.config = {
      id: 'default',
      total_allowed_violation_count: 5,
      rules: [
        { type: ProctoringViolationType.NO_FACE, severity: ViolationSeverity.WARNING, debounce_seconds: 3, action: 'Soft nudge only - no count' },
        { type: ProctoringViolationType.MULTIPLE_FACES, severity: ViolationSeverity.CRITICAL, debounce_seconds: 2, action: 'Warning banner + increment count' },
        { type: ProctoringViolationType.LOOKING_AWAY, severity: ViolationSeverity.CRITICAL, debounce_seconds: 2, action: 'Warning banner + increment count' },
        { type: ProctoringViolationType.TAB_SWITCH, severity: ViolationSeverity.CRITICAL, debounce_seconds: 3, action: 'Warning banner + increment count' },
        { type: ProctoringViolationType.FACE_TOO_FAR, severity: ViolationSeverity.WARNING, debounce_seconds: 3, action: 'Soft nudge only - no count' },
        { type: ProctoringViolationType.EYES_CLOSED, severity: ViolationSeverity.WARNING, debounce_seconds: 3, action: 'Soft nudge only - no count' },
        { type: ProctoringViolationType.BACKGROUND_VOICE, severity: ViolationSeverity.WARNING, debounce_seconds: 3, action: 'Soft nudge only - no count' }
      ]
    };
    this.maxWarnings = 5;
    this.updateThresholdsFromConfig();

    const currentState = this.violationStateSubject.value;
    this.violationStateSubject.next({
      ...currentState,
      maxWarnings: this.maxWarnings
    });
  }

  public setSessionId(id: string | number): void {
    this.currentSessionId = String(id);
  }

  /** Set a separate source for violation snapshots (e.g., Screen Share) */
  public setSnapshotSource(el: HTMLVideoElement): void {
    this.snapshotElement = el;
    console.log('[Proctoring] Snapshot source updated (Evidence will be captured from this source)');
  }

  public setVideoStartTime(time: number): void {
    this.videoStartTime = time;
  }

  async startDetection(videoEl: HTMLVideoElement, sessionId: string = 'default'): Promise<void> {
    if (!this.isBrowser) return;
    this.videoElement = videoEl;
    this.currentSessionId = sessionId;

    // Offscreen canvas for brightness enhancement (low-light pre-processing).
    this.enhancementCanvas = document.createElement('canvas');
    this.enhancementCanvas.width  = 320;
    this.enhancementCanvas.height = 240;
    this.enhancementCtx = this.enhancementCanvas.getContext('2d', { willReadFrequently: true });

    if (!this.config) {
      await this.initializeConfig('default');
    }

    this.isDetecting = true;
    this.runDetectionLoop();
    // Audio proctoring is initialised separately via initAudioProctoring()
    // from interview-panel.ts immediately after getUserMedia — do not re-init here.
  }

  private updateThresholdsFromConfig(): void {
    if (!this.config) return;
    this.config.rules.forEach((rule: ProctoringRule) => {
      const frames = Math.max(1, rule.debounce_seconds * this.fps);
      if (rule.type === ProctoringViolationType.NO_FACE) this.thresholds.warning = frames;
      // Other rules use rule.debounce_seconds directly in checkThresholds if needed, 
      // but here we align the primary thresholds object.
    });
  }

  stopDetection(): void {
    this.isDetecting = false;
    if (this.loopId) {
      clearTimeout(this.loopId);
      this.loopId = null;
    }
    if (this.tabSwitchTimer) {
      clearTimeout(this.tabSwitchTimer);
      this.tabSwitchTimer = null;
    }
    this.videoElement = null;
    this.enhancementCanvas = null;
    this.enhancementCtx = null;
    this.stopAudioProctoring();
  }

  stopAudioProctoring(): void {
    if (this.audioCheckInterval) {
      clearInterval(this.audioCheckInterval);
      this.audioCheckInterval = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.rawAudioStream) {
      this.rawAudioStream.getTracks().forEach(t => t.stop());
      this.rawAudioStream = null;
    }
    this.analyser = null;
    this.bgVoiceSilentCount = 0;
    this.bgVoiceWindow = [];
    this.calibrationFrames = [];
    this.calibrationComplete = false;
    this.candidateVoiceBaseline = 0;
    this.lastCandidateSpeakingAt = 0;
    this.lastDetectionAt = 0;
  }

  private async runDetectionLoop(): Promise<void> {
    if (!this.isDetecting || !this.videoElement || this.videoElement.paused || this.videoElement.ended) {
      if (this.isDetecting) {
        this.loopId = setTimeout(() => this.runDetectionLoop(), 1000 / this.fps);
      }
      return;
    }

    try {
      // For low-light frames, draw the video into the offscreen canvas with increased
      // brightness so face-api can still find the face. If the canvas isn't ready yet,
      // fall back to the raw video element.
      let detectionSource: HTMLVideoElement | HTMLCanvasElement = this.videoElement;
      if (this.enhancementCanvas && this.enhancementCtx) {
        this.enhancementCtx.filter = 'brightness(1.5) contrast(1.2)';
        this.enhancementCtx.drawImage(
          this.videoElement, 0, 0,
          this.enhancementCanvas.width, this.enhancementCanvas.height
        );
        this.enhancementCtx.filter = 'none';
        detectionSource = this.enhancementCanvas;
      }

      // inputSize: 512 — higher resolution improves detection in poor lighting
      // scoreThreshold: 0.15 — lower threshold reduces false NO_FACE positives in dim light
      const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.15 });
      const detections = await faceapi
        .detectAllFaces(detectionSource, options)
        .withFaceLandmarks();

      // 🔥 IMPORTANT DEBUG LOG
      console.log('[Detection Faces Count]:', detections.length);

      this.evaluateDetections(detections);

    } catch (err) {
      console.error('Detection error:', err);
    }

    if (this.isDetecting) {
      this.loopId = setTimeout(() => this.runDetectionLoop(), 1000 / this.fps);
    }
  }

  private evaluateDetections(detections: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68>[]): void {
    const currentState = this.violationStateSubject.value;
    if (currentState.isTerminated) return;

    // --- NO FACE ---
    if (detections.length === 0) {
      this.violationCounters.NO_FACE++;
      this.lastDetection = null;
    } else {
      // Instant reset: face is back, clear counter immediately
      this.violationCounters.NO_FACE = 0;
    }

    // --- MULTIPLE FACES ---
    // Filter to detections that look like a real second person, not hands/objects:
    //   - height > 15% of video height: a real face (even at a distance) occupies at least
    //     this much; raised hands and fingers are typically smaller or similarly sized
    //     but fail the score check below.
    //   - score > 0.5: hands produce low-confidence detections (~0.2–0.35); a genuine
    //     face registers much higher confidence with TinyFaceDetector.
    const videoHeight = this.videoElement?.videoHeight || 480;
    // Require height > 20% of frame AND confidence > 0.72 to filter out fans/objects.
    // Objects (fans, plants, posters) typically score 0.2–0.45 with TinyFaceDetector;
    // a real face at normal interview distance scores 0.65+.
    const significantFaces = detections.filter(d =>
      d.detection.box.height > videoHeight * 0.20 &&
      d.detection.score > 0.72
    );

    if (significantFaces.length > 1) {
      this.violationCounters.MULTIPLE_FACES++;
      this.lastDetection = null;
    } else {
      // Instant reset: intermittent fan/object detections must be sustained every frame
      // to trigger the threshold. A single missed frame clears the counter so brief
      // false positives (fan blade crossing camera FOV) never accumulate.
      this.violationCounters.MULTIPLE_FACES = 0;
    }

    if (detections.length === 1) {
      this.lastDetection = detections[0];
      this.lastDetectionAt = Date.now();
      const landmarks = detections[0].landmarks;

      // --- LOOKING AWAY ---
      if (this.isLookingAway(landmarks)) {
        this.violationCounters.LOOKING_AWAY++;
      } else {
        if (this.violationCounters.LOOKING_AWAY > 0) this.violationCounters.LOOKING_AWAY--;
      }

      // --- EYES CLOSED ---
      if (this.isEyesClosed(landmarks)) {
        this.violationCounters.EYES_CLOSED++;
      } else {
        if (this.violationCounters.EYES_CLOSED > 0) this.violationCounters.EYES_CLOSED--;
      }

      // --- FACE TOO FAR ---
      if (this.isFaceTooFar(detections[0].detection)) {
        this.violationCounters.FACE_TOO_FAR++;
      } else {
        if (this.violationCounters.FACE_TOO_FAR > 0) this.violationCounters.FACE_TOO_FAR--;
      }

    }


    this.checkThresholds();
  }
  public dismissWarning(type: string): void {
    const currentState = this.violationStateSubject.value;
    const normalizedType = String(type).toUpperCase();
    const now = Date.now();

    console.log(`[Proctoring] Dismissing warning manually for: ${normalizedType}`);

    this.violationStateSubject.next({
      ...currentState,
      currentWarning: null
    });

    this.gracePeriodMap.set(normalizedType, now);
  }

  private isInGracePeriod(type: string): boolean {
    const normalizedType = String(type).toUpperCase();
    const lastDismissed = this.gracePeriodMap.get(normalizedType);
    if (!lastDismissed) return false;

    const timeSinceDismissal = Date.now() - lastDismissed;
    const inGrace = timeSinceDismissal < this.GRACE_PERIOD_MS;

    if (inGrace) {
      console.log(`[Proctoring] Suppressing popup for ${normalizedType} (in grace period for another ${this.GRACE_PERIOD_MS - timeSinceDismissal}ms)`);
    }
    return inGrace;
  }

  private isLookingAway(landmarks: faceapi.FaceLandmarks68): boolean {
    const nose = landmarks.getNose();
    const jawOutline = landmarks.getJawOutline();
    if (!nose || !jawOutline || nose.length === 0 || jawOutline.length === 0) return false;

    const noseTip = nose[3];
    const leftEdge = jawOutline[0];
    const rightEdge = jawOutline[jawOutline.length - 1];
    const distLeft = noseTip.x - leftEdge.x;
    const distRight = rightEdge.x - noseTip.x;
    const ratio = distLeft / distRight;

    // Tightened from 4/0.25 → 2.5/0.40: the old range missed moderate gaze shifts.
    // Values outside 0.40–2.5 reliably indicate the nose has crossed the face midline.
    return ratio > 2.5 || ratio < 0.40;
  }

  private isEyesClosed(landmarks: faceapi.FaceLandmarks68): boolean {
    const leftEye = landmarks.getLeftEye();
    const rightEye = landmarks.getRightEye();

    const getEyeAspectRatio = (eye: faceapi.Point[]) => {
      const vertical1 = Math.hypot(eye[1].x - eye[5].x, eye[1].y - eye[5].y);
      const vertical2 = Math.hypot(eye[2].x - eye[4].x, eye[2].y - eye[4].y);
      const horizontal = Math.hypot(eye[0].x - eye[3].x, eye[0].y - eye[3].y);
      return (vertical1 + vertical2) / (2.0 * horizontal);
    };

    const leftEAR = getEyeAspectRatio(leftEye);
    const rightEAR = getEyeAspectRatio(rightEye);

    const avgEAR = (leftEAR + rightEAR) / 2;
    // Spectacle frames occlude upper eyelid landmarks, compressing EAR. 0.25 caused
    // false positives for glasses-wearers; 0.18 only fires when eyes are genuinely shut.
    return avgEAR < 0.18;
  }

  private isFaceTooFar(detection: faceapi.FaceDetection): boolean {
    const box = detection.box;
    if (!this.videoElement) return false;

    const ratio = box.height / this.videoElement.videoHeight;
    // At a normal desk distance on a 640×480 stream a face occupies ~15–22% of height.
    // 0.26 was triggering on every normally-seated candidate. 0.13 only fires when the
    // person is genuinely far from the camera or the face is very small.
    return ratio < 0.13;
  }

  private checkThresholds(): void {
    const currentState = this.violationStateSubject.value;
    if (currentState.isTerminated) return;

    const getRuleFrames = (type: string) => {
      const rule = this.config?.rules.find(r => r.type === type);
      return rule ? rule.debounce_seconds * this.fps : 3 * this.fps;
    };

    // NO_FACE
    const noFaceLimit = getRuleFrames(ProctoringViolationType.NO_FACE);
    if (this.violationCounters.NO_FACE >= noFaceLimit) {
      this.triggerViolation(ProctoringViolationType.NO_FACE, ViolationSeverity.WARNING);
      this.violationCounters.NO_FACE = 0;
      return; // Stop checking other violations this frame
    }

    // MULTIPLE_FACES
    const multiLimit = getRuleFrames(ProctoringViolationType.MULTIPLE_FACES);
    if (this.violationCounters.MULTIPLE_FACES >= multiLimit) {
      this.triggerViolation(ProctoringViolationType.MULTIPLE_FACES, ViolationSeverity.CRITICAL);
      this.violationCounters.MULTIPLE_FACES = 0;
      return;
    }

    // LOOKING_AWAY
    const lookingLimit = getRuleFrames(ProctoringViolationType.LOOKING_AWAY);
    if (this.violationCounters.LOOKING_AWAY >= lookingLimit) {
      this.triggerViolation(ProctoringViolationType.LOOKING_AWAY, ViolationSeverity.CRITICAL);
      this.violationCounters.LOOKING_AWAY = 0;
      return;
    }

    // EYES_CLOSED
    const eyesLimit = getRuleFrames(ProctoringViolationType.EYES_CLOSED);
    if (this.violationCounters.EYES_CLOSED >= eyesLimit) {
      this.triggerViolation(ProctoringViolationType.EYES_CLOSED, ViolationSeverity.WARNING);
      this.violationCounters.EYES_CLOSED = 0;
      return;
    }

    // FACE_TOO_FAR
    const farLimit = getRuleFrames(ProctoringViolationType.FACE_TOO_FAR);
    if (this.violationCounters.FACE_TOO_FAR >= farLimit) {
      this.triggerViolation(ProctoringViolationType.FACE_TOO_FAR, ViolationSeverity.WARNING);
      this.violationCounters.FACE_TOO_FAR = 0;
      return;
    }
  }
  public triggerViolation(type: ProctoringViolationType | string, severity: ViolationSeverity): void {
    const currentState = this.violationStateSubject.value;
    if (currentState.isTerminated) return;

    const normalizedType = String(type).toUpperCase(); // ✅ normalize

    const ruleConfig = this.config?.rules.find(r => String(r.type).toUpperCase() === normalizedType);

    // Normalize severity
    let actualSeverity: ViolationSeverity = severity;
    if (ruleConfig) {
      const severityStr = String(ruleConfig.severity).toUpperCase();
      if (severityStr === 'CRITICAL') actualSeverity = ViolationSeverity.CRITICAL;
      else if (severityStr === 'WARNING') actualSeverity = ViolationSeverity.WARNING;
      else if (severityStr === 'INFO') actualSeverity = ViolationSeverity.INFO;
    }

    // TRUE GRACE PERIOD:
    // If the user just dismissed this exact violation, they are in the grace period.
    // We completely ignore this trigger to give them the 3 seconds to fix their behavior.
    // This prevents "hidden strikes" accumulating in the background right after clicking Dismiss.
    if (this.isInGracePeriod(normalizedType) && actualSeverity !== ViolationSeverity.INFO) {
      return;
    }

    // Capture snapshot
    this.captureAndUploadSnapshot(normalizedType, actualSeverity);

    // TAB SWITCH handling
    if (normalizedType === 'TAB_SWITCH') {
      if (this.tabSwitchTimer) return;
      this.tabSwitchTimer = setTimeout(() => {
        this.terminateSession(TerminationReason.TAB_SWITCH_TIMEOUT);
      }, 15000);
    }

    const now = Date.now();
    this.lastWarningAtMap.set(normalizedType, now);
    const nextWarning = normalizedType;

    // =========================
    // WARNING / INFO
    // =========================
    if (actualSeverity === ViolationSeverity.WARNING || actualSeverity === ViolationSeverity.INFO) {
      const shouldIncrement = ruleConfig
        ? !ruleConfig.action.toLowerCase().includes('no count')
        : true;

      const newWarningCount =
        (shouldIncrement && actualSeverity === ViolationSeverity.WARNING)
          ? currentState.warningCount + 1
          : currentState.warningCount;

      if (shouldIncrement && actualSeverity === ViolationSeverity.WARNING) {
        console.log(`[Proctoring] Strike recorded: ${newWarningCount} of ${this.maxWarnings} (${normalizedType})`);
      }

      // Only update currentWarning if:
      // 1. There is no active warning OR
      // 2. The new violation is of higher or equal severity to the current one
      const currentSeverity = this.getSeverityOf(currentState.currentWarning);
      const shouldUpdateUI = !currentState.currentWarning || this.isSeverityHigherOrEqual(actualSeverity, currentSeverity);

      this.violationStateSubject.next({
        ...currentState,
        warningCount: newWarningCount,
        currentWarning: shouldUpdateUI ? nextWarning : currentState.currentWarning
      });

      if (shouldIncrement && newWarningCount >= this.maxWarnings) {
        this.terminateSession(TerminationReason.MAX_WARNINGS_EXCEEDED);
      }
    }

    // =========================
    // CRITICAL
    // =========================
    else if (actualSeverity === ViolationSeverity.CRITICAL) {
      const shouldStrike = ruleConfig
        ? ruleConfig.action.toLowerCase().includes('increment count')
        : false;

      if (shouldStrike) {
        const newWarningCount = currentState.warningCount + 1;
        console.log(`[Proctoring] CRITICAL Strike recorded: ${newWarningCount} of ${this.maxWarnings} (${normalizedType})`);

        this.violationStateSubject.next({
          ...currentState,
          warningCount: newWarningCount,
          currentWarning: nextWarning // ✅ already correct
        });

        if (newWarningCount >= this.maxWarnings) {
          this.terminateSession(TerminationReason.MAX_WARNINGS_EXCEEDED);
        }
      } else {
        this.violationStateSubject.next({
          ...currentState,
          currentWarning: nextWarning
        });

        this.terminateSession(TerminationReason.CRITICAL_VIOLATION);
      }
    }
  }

  private async captureAndUploadSnapshot(type: string, severity: string): Promise<void> {
    const sourceEl = this.snapshotElement || this.videoElement;
    if (!sourceEl) return;

    const now = Date.now();
    const lastSnapshot = this.lastSnapshotAtMap.get(type) || 0;
    if (now - lastSnapshot < 5000) return;
    this.lastSnapshotAtMap.set(type, now);

    // Fallback if session ID not set yet
    const sessionId = (this.currentSessionId && this.currentSessionId !== 'unknown')
      ? this.currentSessionId
      : this.sessionService.value?.session_id || sessionStorage.getItem('current_ai_session_id') || 'unknown';

    let videoTimestamp = '00:00';
    if (this.videoStartTime) {
      const elapsedMs = now - this.videoStartTime;
      const totalSeconds = Math.floor(elapsedMs / 1000);

      const hrs = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (hrs > 0) {
        videoTimestamp = `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      } else {
        videoTimestamp = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = sourceEl.videoWidth || 640;
      canvas.height = sourceEl.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(sourceEl, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) return;

        console.log(`[Proctoring] Attempting evidence snapshot upload for ${type}...`);

        const reasonMap: Record<string, string> = {
          'No face': 'No candidate face identified within the camera frame.',
          'Multiple faces': 'Presence of more than one individual detected in the frame.',
          'Looking away': 'Candidate’s gaze has shifted away from the examination interface.',
          'Tab switch': 'Navigation to another browser tab or external application detected during the session.',
          'Face too far': 'Candidate positioned outside the optimal camera distance.',
          'Eyes closed': 'Candidate’s eyes not clearly visible for a sustained period.',
          'Screen share stopped': 'Screen sharing was interrupted during the session.',
          'Background voice': 'Audio energy detected while candidate was not speaking — possible second person in the room.'
        };

        const displayKey = type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, ' ');

        console.log(`[Proctoring] Snapshot Evidence Payload: type=${type}, is_violation=true, session_id=${sessionId}`);

        this.sessionService.uploadSnapshot({
          session_id: sessionId,
          image: blob,
          is_violation: true, // Explicitly set to true for violation evidence
          violation_type: this.formatViolationType(type),
          severity: severity,
          reason: reasonMap[displayKey] || `Proctoring violation initiated: ${displayKey}`,
          violation_count: this.violationStateSubject.value.warningCount,
          video_timestamp: videoTimestamp
        }).subscribe({
          next: (res: any) => console.log(`[Proctoring] Snapshot successfully uploaded for ${type}. Response:`, res),
          error: (err: any) => console.error(`[Proctoring] Snapshot upload FAILED for ${type}`, err)
        });
      }, 'image/jpeg', 0.8);
    } catch (err) {
      console.error('[Proctoring] Canvas capture failed:', err);
    }
  }
  private formatViolationType(type: string): string {
    const map: Record<string, string> = {
      'NO_FACE': 'No face',
      'MULTIPLE_FACES': 'Multiple faces',
      'LOOKING_AWAY': 'Looking away',
      'TAB_SWITCH': 'Tab switch',
      'FACE_TOO_FAR': 'Face too far',
      'EYES_CLOSED': 'Eyes closed',
      'BACKGROUND_VOICE': 'Background voice'
    };
    return map[type] || type.charAt(0).toUpperCase() + type.slice(1).toLowerCase().replace(/_/g, ' ');
  }

  private getSeverityOf(type: ProctoringViolationType | string | null): ViolationSeverity {
    if (!type) return ViolationSeverity.INFO;
    const rule = this.config?.rules.find(r => r.type === String(type).toUpperCase());
    return rule ? rule.severity : ViolationSeverity.WARNING;
  }

  private isSeverityHigherOrEqual(a: ViolationSeverity, b: ViolationSeverity): boolean {
    const map = {
      [ViolationSeverity.INFO]: 0,
      [ViolationSeverity.WARNING]: 1,
      [ViolationSeverity.CRITICAL]: 2
    };
    return map[a] >= map[b];
  }
  public resetTabSwitchTimer(): void {
    if (this.tabSwitchTimer) {
      clearTimeout(this.tabSwitchTimer);
      this.tabSwitchTimer = null;
    }
  }

  /**
   * Pause/resume background-voice analysis.
   * Call with true when the AI starts speaking, false when it stops.
   * The raw mic stream picks up the AI's room echo which corrupts the baseline
   * and produces false BACKGROUND_VOICE violations — pausing prevents this.
   */
  public setAudioAnalysisPaused(paused: boolean): void {
    this.audioAnalysisPaused = paused;
    if (!paused) {
      // Clear accumulated state so the AI's echo doesn't carry over into the next analysis window.
      this.bgVoiceSilentCount = 0;
      this.bgVoiceWindow = [];
    }
  }

  async initAudioProctoring(stream: MediaStream): Promise<void> {
    if (!this.isBrowser) return;
    try {
      // Get a raw (unprocessed) audio stream for analysis — the WebRTC stream has
      // echoCancellation + noiseSuppression enabled which strips out background voices
      // before our analyser ever sees them. A separate raw stream bypasses those filters.
      let analysisStream: MediaStream = stream;
      try {
        const rawStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
        });
        this.rawAudioStream = rawStream;
        analysisStream = rawStream;
        console.log('[Proctoring] Raw audio stream acquired for analysis');
      } catch (rawErr) {
        console.warn('[Proctoring] Could not get raw stream, falling back to WebRTC stream', rawErr);
      }

      this.audioContext = new AudioContext();
      // AudioContext may be suspended by browser autoplay policy — force resume.
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const source = this.audioContext.createMediaStreamSource(analysisStream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;
      source.connect(this.analyser);
      this.audioCheckInterval = setInterval(() => this.analyseAudioFrame(), 500);
      console.log('[Proctoring] Audio proctoring initialised (context state:', this.audioContext.state, ')');
    } catch (err) {
      console.warn('[Proctoring] Audio proctoring init failed', err);
    }
  }

  private analyseAudioFrame(): void {
    if (!this.analyser || !this.audioContext) return;
    if (this.audioAnalysisPaused) return;
    const currentState = this.violationStateSubject.value;
    if (currentState.isTerminated) return;

    // Broadband RMS — quick filter for near-total silence
    const timeData = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(timeData);
    const rms = Math.sqrt(timeData.reduce((s, v) => s + v * v, 0) / timeData.length);
    if (rms < 0.01) {
      this.bgVoiceSilentCount = 0;
      this.bgVoiceWindow = [];
      return;
    }

    // Voice-band energy (300–3400 Hz) — discriminates human speech from fan/traffic noise.
    // Fan/vehicle noise is mostly low-frequency broadband and produces very low voice-band energy.
    const voiceEnergy = this.computeVoiceBandEnergy();

    const candidateSpeaking = this.isCandidateSpeaking();

    // ─── SCENARIO 2: Both candidate and another person speaking ───────────────
    if (candidateSpeaking) {
      this.lastCandidateSpeakingAt = Date.now();
      this.bgVoiceSilentCount = 0;

      if (voiceEnergy < 0.00005) return; // ignore near-silence frames

      // ── Calibration phase ──────────────────────────────────────────────────
      // Collect the first CALIBRATION_FRAME_COUNT speaking frames (AI-echo-free
      // because analysis is paused during AI speech). Compute their median as the
      // fixed baseline. Median is used instead of mean so that a handful of loud
      // outlier frames don't skew the reference level.
      // No violation can trigger during calibration.
      if (!this.calibrationComplete) {
        this.calibrationFrames.push(voiceEnergy);
        if (this.calibrationFrames.length >= this.CALIBRATION_FRAME_COUNT) {
          const sorted = [...this.calibrationFrames].sort((a, b) => a - b);
          this.candidateVoiceBaseline = sorted[Math.floor(sorted.length / 2)];
          this.calibrationComplete = true;
          console.log(`[Proctoring] Audio baseline calibrated: ${this.candidateVoiceBaseline.toFixed(5)}`);
        } else {
          console.log(`[Audio] calibrating (${this.calibrationFrames.length}/${this.CALIBRATION_FRAME_COUNT}) — energy: ${voiceEnergy.toFixed(5)}`);
        }
        return;
      }

      // ── Detection phase ────────────────────────────────────────────────────
      const ratio = voiceEnergy / this.candidateVoiceBaseline;
      console.log(`[Audio] speaking — energy: ${voiceEnergy.toFixed(5)}, baseline: ${this.candidateVoiceBaseline.toFixed(5)}, ratio: ${ratio.toFixed(2)}`);

      const isHit = ratio > 2.2;
      this.bgVoiceWindow.push(isHit);
      if (this.bgVoiceWindow.length > this.BG_VOICE_WINDOW_SIZE) {
        this.bgVoiceWindow.shift();
      }
      const hitCount = this.bgVoiceWindow.filter(Boolean).length;
      if (hitCount >= this.BG_VOICE_WINDOW_HITS) {
        this.bgVoiceWindow = [];
        console.warn(`[Proctoring] MULTI-VOICE detected (ratio: ${ratio.toFixed(2)}, hits: ${hitCount}/${this.BG_VOICE_WINDOW_SIZE})`);
        this.triggerAudioViolation();
      }
      return;
    }

    // ─── SCENARIO 1: Candidate silent, another person speaking ───────────────
    this.bgVoiceWindow = []; // Clear scenario 2 window when candidate is silent

    // Grace period absorbs echo/reverb after candidate stops speaking.
    const timeSinceSpeech = Date.now() - this.lastCandidateSpeakingAt;
    if (timeSinceSpeech < this.POST_SPEECH_GRACE_MS) {
      this.bgVoiceSilentCount = 0;
      return;
    }

    const VOICE_BAND_THRESHOLD = 0.0005;
    if (voiceEnergy > VOICE_BAND_THRESHOLD) {
      this.bgVoiceSilentCount++;
      if (this.bgVoiceSilentCount >= this.BG_SILENT_FRAME_THRESHOLD) {
        this.bgVoiceSilentCount = 0;
        console.warn(`[Proctoring] BACKGROUND VOICE (candidate silent, voiceEnergy: ${voiceEnergy.toFixed(4)}, RMS: ${rms.toFixed(4)})`);
        this.triggerAudioViolation();
      }
    } else {
      this.bgVoiceSilentCount = 0;
    }
  }

  private computeVoiceBandEnergy(): number {
    if (!this.analyser || !this.audioContext) return 0;
    const freqData = new Float32Array(this.analyser.frequencyBinCount);
    this.analyser.getFloatFrequencyData(freqData);
    // fftSize=2048, so frequencyBinCount=1024. binHz = sampleRate / fftSize.
    const binHz = this.audioContext.sampleRate / this.analyser.fftSize;
    const voiceLow = Math.floor(300 / binHz);
    const voiceHigh = Math.ceil(3400 / binHz);
    let energy = 0;
    let count = 0;
    for (let i = voiceLow; i <= voiceHigh && i < freqData.length; i++) {
      if (isFinite(freqData[i])) {
        // freqData is in dBFS — convert to linear amplitude
        energy += Math.pow(10, freqData[i] / 20);
        count++;
      }
    }
    return count > 0 ? energy / count : 0;
  }

  private isCandidateSpeaking(): boolean {
    // No face data at all — safe default: don't flag as background voice
    if (!this.lastDetection) return true;

    // Stale face data (>1s old) — treat as speaking to avoid false positives
    if (Date.now() - this.lastDetectionAt > 1000) return true;

    const mouth = this.lastDetection.landmarks.getMouth();
    // Bad landmark data — safe default
    if (!mouth || mouth.length < 10) return true;

    const jawOpen = Math.abs(mouth[9].y - mouth[3].y);
    // 12px covers normal conversational speech (closed-mouth speaking registers ~8-10px).
    // 22 was too high — many speaking frames were classified as "silent", causing
    // the candidate's own voice to be flagged by the Scenario 1 detector.
    return jawOpen > 12;
  }

  private triggerAudioViolation(): void {
    const DEBOUNCE_MS = 3000;
    const lastTrigger = this.lastWarningAtMap.get('BACKGROUND_VOICE') || 0;
    if (Date.now() - lastTrigger < DEBOUNCE_MS) return;
    this.triggerViolation(ProctoringViolationType.BACKGROUND_VOICE, ViolationSeverity.WARNING);
  }

  private terminateSession(reason: TerminationReason | string): void {
    this.stopDetection();
    const currentState = this.violationStateSubject.value;
    this.violationStateSubject.next({
      ...currentState,
      isTerminated: true,
      terminationReason: reason
    });
  }

  ngOnDestroy(): void {
    this.stopDetection();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
